import {
  AlertCircle,
  ArrowLeftToLine,
  CalendarCheck,
  RotateCcw,
} from "lucide-react";
import { useFeature } from "@/lib/use-feature";
import { AgendaMiniCalendar } from "./AgendaMiniCalendar";
import { DentistCheckboxList } from "./DentistSelector";
import { ServiceCheckboxList } from "./ServiceSelector";

type DentistFilter = {
  ids: string[];
  toggle: (id: string) => void;
  selectAll: (allIds: string[]) => void;
  clear: () => void;
};

type ServiceFilter = DentistFilter;

type Props = {
  selectedDate: Date;
  onSelectDate: (d: Date) => void;
  onClose: () => void;
  dentistFilter: DentistFilter;
  dentistSettings: Record<string, { color?: string; title?: string }>;
  serviceFilter: ServiceFilter;
};

export function AgendaSidebar({
  selectedDate,
  onSelectDate,
  onClose,
  dentistFilter,
  dentistSettings,
  serviceFilter,
}: Props) {
  const hasClinicServices = useFeature("clinicServices");
  return (
    <aside className="hidden w-60 shrink-0 flex-col border-r border-zinc-200 bg-zinc-50/60 md:flex dark:border-border dark:bg-card/40">
      <div className="space-y-2 p-3">
        <AgendaMiniCalendar
          selectedDate={selectedDate}
          onSelect={onSelectDate}
          size="compact"
        />
        <button
          type="button"
          onClick={onClose}
          className="inline-flex h-8 w-full items-center justify-center gap-1.5 rounded-md border border-zinc-200 bg-white px-3 text-[11px] font-medium text-zinc-600 hover:border-zinc-300 hover:bg-zinc-50 dark:border-border dark:bg-card dark:text-foreground dark:hover:bg-muted"
        >
          <ArrowLeftToLine className="h-3.5 w-3.5" />
          Ocultar calendário
        </button>
      </div>

      <div className="border-t border-zinc-200 dark:border-border" />

      <div className="px-3 py-2.5">
        <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-muted-foreground">
          Profissional
        </p>
        <DentistCheckboxList
          selectedIds={dentistFilter.ids}
          onToggle={dentistFilter.toggle}
          onSelectAll={dentistFilter.selectAll}
          onClear={dentistFilter.clear}
          dentistSettings={dentistSettings}
        />
      </div>

      {hasClinicServices && (
        <>
          <div className="border-t border-zinc-200 dark:border-border" />

          <div className="px-3 py-2.5">
            <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-muted-foreground">
              Procedimento
            </p>
            <ServiceCheckboxList
              selectedIds={serviceFilter.ids}
              onToggle={serviceFilter.toggle}
              onSelectAll={serviceFilter.selectAll}
              onClear={serviceFilter.clear}
            />
          </div>
        </>
      )}

      <div className="border-t border-zinc-200 dark:border-border" />

      <div className="space-y-0.5 px-3 py-2.5">
        <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-muted-foreground">
          Filtros rápidos
        </p>
        <QuickFilter
          icon={<RotateCcw className="h-4 w-4" />}
          label="Retornos"
          count="—"
          color="#3B82F6"
        />
        <QuickFilter
          icon={<AlertCircle className="h-4 w-4" />}
          label="Urgências"
          count="—"
          color="#EF4444"
        />
        <QuickFilter
          icon={<CalendarCheck className="h-4 w-4" />}
          label="Confirmados"
          count="—"
          color="#10B981"
        />
      </div>

      <div className="flex-1" />
    </aside>
  );
}

function QuickFilter({
  icon,
  label,
  count,
  color,
}: {
  icon: React.ReactNode;
  label: string;
  count: string | number;
  color: string;
}) {
  return (
    <button
      type="button"
      className="flex w-full items-center justify-between rounded-md px-2 py-1.5 text-xs text-zinc-700 transition-colors hover:bg-brand-50 hover:text-brand-700 dark:text-foreground/80 dark:hover:bg-primary/10 dark:hover:text-primary"
    >
      <div className="flex items-center gap-2">
        <span style={{ color }}>{icon}</span>
        <span>{label}</span>
      </div>
      <span className="text-zinc-400 dark:text-muted-foreground/70">{count}</span>
    </button>
  );
}
